import { Visibility } from '@mui/icons-material'
import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined'
import { v4 as uuidv4 } from 'uuid'

import { Box, Button, FormControlLabel, Grid, IconButton, Switch, Typography } from '@mui/material'
import React, { useState } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useGetCountriesQuery } from '../../../app/redux-fetch/apiCoreQuery'
import {
  useGetAllLoadTypesQuery,
  useGetCurrenciesByCustomerIdQuery,
} from '../../../app/redux-fetch/apiOrders'
import { formatValue } from '../../../app/utils'
import { useConfirmDialogContext } from '../../../components/ConfirmDialog'
import DefaultSpacingGrid from '../../../components/Containers/DefaultSpacingGrid'
import ToggleContainer from '../../../components/Containers/ToggleContainer'
import FormInput from '../../../components/Forms/FormInput'
import MRTDataGridv2 from '../../../components/MRTDataGrid/MRTDataGridv2'
import InfoDisplayItem from '../../../components/Typographies/InfoDisplayItem'
import SideModalWrapper from '../../Assets/modals/SideModalWrapper'
import { useOrderFormContext } from '../context/OrderFormContext'
import LoadContentTabs from './LoadContent/LoadContentTabs'
import messages from './messages'
import ProductForm from './ProductForm'

interface PackageContentFormProps {
  consignmentIndex: number
  packagingIndex: number
  isOpenPackageContent: boolean
  handleTogglePackageContent: (action: any) => void
  disabled?: boolean
}

const PackageContentForm: React.FC<PackageContentFormProps> = ({
  consignmentIndex,
  packagingIndex,
  isOpenPackageContent,
  handleTogglePackageContent,
  disabled = false,
}) => {
  const { control, setValue, watch, unregister, getValues } = useFormContext()
  const { formatMessage } = useIntl()
  const { variant } = useOrderFormContext()
  const { data: countries = [] } = useGetCountriesQuery()
  const { confirm } = useConfirmDialogContext()

  const [isOpenSummaryContent, setIsOpenSummaryContent] = useState(true)

  const {
    fields,
    append: appendProduct,
    remove: removeProduct,
  } = useFieldArray({
    control,
    name: `bookings.${consignmentIndex}.packaging.${packagingIndex}.products`,
  })

  const customerId = watch('customerId')

  const { data: currencies = [] } = useGetCurrenciesByCustomerIdQuery({
    customerId: customerId ?? 0,
    hideDisabled: true,
  })

  const products = watch(`bookings.${consignmentIndex}.packaging.${packagingIndex}.products`, [])

  const currencyId = watch(`bookings.${consignmentIndex}.currencyId`)
  const loadTypeId = watch(`bookings.${consignmentIndex}.loadTypeId`)

  const { data: packagingTypes = [] } = useGetAllLoadTypesQuery()

  const loadType = packagingTypes?.find((type: any) => type.id === loadTypeId)?.name ?? ''
  const loadTypeString = Array.isArray(loadType) ? loadType.join(', ') : String(loadType)
  const useDeclaredValue = watch(
    `bookings.${consignmentIndex}.packaging.${packagingIndex}.useDeclaredValue`,
    false
  )

  const hasDataToLose = () => {
    if (useDeclaredValue) {
      const currentDeclaredContentDescription = getValues(
        `bookings.${consignmentIndex}.packaging.${packagingIndex}.declaredContentDescription`
      )
      const currentDeclaredValue = getValues(
        `bookings.${consignmentIndex}.packaging.${packagingIndex}.declaredValue`
      )

      return currentDeclaredContentDescription || currentDeclaredValue
    } else {
      const currentProducts = getValues(
        `bookings.${consignmentIndex}.packaging.${packagingIndex}.products`
      )

      if (!currentProducts || currentProducts.length === 0) {
        return false
      }

      const defaultProductValues = {
        batchDetails: [],
        countryOfOriginId: null,
        physicalProperties: null,
        productDescription: '',
        quantity: 1,
        temperatureControlled: false,
        temperatureSetting: null,
        totalValue: undefined,
        unitPrice: 0,
        useProduct: false,
      }

      const hasNonDefaultProducts = currentProducts.some((product: any) => {
        if (!product) return false

        return (
          product.productDescription !== defaultProductValues.productDescription ||
          product.quantity !== defaultProductValues.quantity ||
          product.unitPrice !== defaultProductValues.unitPrice ||
          product.countryOfOriginId !== defaultProductValues.countryOfOriginId ||
          product.temperatureControlled !== defaultProductValues.temperatureControlled ||
          product.useProduct !== defaultProductValues.useProduct ||
          (product.batchDetails && product.batchDetails.length > 0) ||
          product.physicalProperties !== defaultProductValues.physicalProperties ||
          product.temperatureSetting !== defaultProductValues.temperatureSetting ||
          product.totalValue !== defaultProductValues.totalValue
        )
      })

      return hasNonDefaultProducts
    }
  }

  const resetDeclaredValues = () => {
    unregister(
      `bookings.${consignmentIndex}.packaging.${packagingIndex}.declaredContentDescription`
    )
    unregister(`bookings.${consignmentIndex}.packaging.${packagingIndex}.declaredValue`)
    unregister(`bookings.${consignmentIndex}.packaging.${packagingIndex}.products`)
  }

  const handleRemoveProduct = (index: number) => {
    if (fields.length === 1) return
    removeProduct(index)
  }

  const handleToggleDeclaredValue = (checked: boolean) => {
    resetDeclaredValues()
    if (!checked) {
      setValue(`bookings.${consignmentIndex}.packaging.${packagingIndex}.products`, [])
      appendProduct({
        internalId: uuidv4(),
        productDescription: '',
        quantity: 1,
        unitPrice: 0,
        countryOfOriginId: null,
        totalValue: undefined,
        useProduct: false,
        temperatureControlled: false,
        physicalProperties: null,
        temperatureSetting: null,
      })
    } else {
      setValue(`bookings.${consignmentIndex}.packaging.${packagingIndex}.declaredValue`, null)
      setValue(
        `bookings.${consignmentIndex}.packaging.${packagingIndex}.declaredContentDescription`,
        null
      )
    }
  }

  const handleToggleWithConfirmation = async (checked: boolean) => {
    if (hasDataToLose()) {
      const confirmed = await confirm({
        title: formatMessage(messages.confirmToggleModeTitle),
        message: formatMessage(messages.confirmToggleModeMessage),
        confirmText: formatMessage(messages.confirmToggleModeYes),
        cancelText: formatMessage(messages.confirmToggleModeNo),
        severity: 'warning',
      })

      if (!confirmed) {
        return
      }
    }

    const newUseDeclaredValue = !checked

    setValue(
      `bookings.${consignmentIndex}.packaging.${packagingIndex}.useDeclaredValue`,
      newUseDeclaredValue
    )
    handleToggleDeclaredValue(newUseDeclaredValue)
  }

  const handleAddProductIfRequired = () => {
    if (products && products.length === 0 && !useDeclaredValue)
      appendProduct({
        internalId: uuidv4(),
        productDescription: '',
        quantity: 1,
        unitPrice: 0,
        totalValue: undefined,
        currency: '',
        useProduct: false,
      })
  }

  const [editProductIndex, setEditProductIndex] = React.useState<number | null>(null)
  const [isEditModalOpen, setEditModalOpen] = React.useState(false)
  const handleOpenEditModal = (idx: number) => {
    setEditProductIndex(idx)
    setEditModalOpen(true)
  }
  const handleCloseEditModal = () => {
    setEditModalOpen(false)
    setEditProductIndex(null)
  }

  if (variant === 'summary') {
    const getCurrencySymbol = (id: number | undefined) => {
      if (!id) return ''
      const found = currencies.find((c: any) => c.id === id)
      return found ? found.localisedSymbol : ''
    }
    const appendCurrency = (val: any, symbol: string) => {
      if (val === undefined || val === null || val === '' || val === 'N/A') return 'N/A'
      return symbol ? `${val} ${symbol}` : val
    }
    return (
      <Box>
        <ToggleContainer
          title={formatMessage(messages.packageContent, { loadType: loadTypeString })}
          isOpen={isOpenSummaryContent}
          handleToggle={() => setIsOpenSummaryContent((prev) => !prev)}
          sx={{ mb: '8px', pb: '8px' }}
          isGrey={true}
        >
          <DefaultSpacingGrid container>
            <Grid item xs={12} md={6}>
              <InfoDisplayItem
                label={formatMessage(messages.detailLevel)}
                value={
                  useDeclaredValue
                    ? formatMessage(messages.declaredValue)
                    : formatMessage(messages.specifiedContents)
                }
                isSmallScreen={false}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoDisplayItem
                label={formatMessage(messages.isHazardous)}
                value={
                  watch(`bookings.${consignmentIndex}.packaging.${packagingIndex}.isHazardous`)
                    ? 'Yes'
                    : 'No'
                }
                isSmallScreen={false}
              />
            </Grid>
            {useDeclaredValue && (
              <>
                <Grid item xs={12} md={6}>
                  <InfoDisplayItem
                    label={formatMessage(messages.contentDescription)}
                    value={
                      watch(
                        `bookings.${consignmentIndex}.packaging.${packagingIndex}.declaredContentDescription`
                      ) || 'N/A'
                    }
                    isSmallScreen={false}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <InfoDisplayItem
                    label={formatMessage(messages.totalValue)}
                    value={appendCurrency(
                      formatValue(
                        watch(
                          `bookings.${consignmentIndex}.packaging.${packagingIndex}.declaredValue`
                        )
                      ),
                      getCurrencySymbol(currencyId) || ''
                    )}
                    isSmallScreen={false}
                  />
                </Grid>
              </>
            )}
          </DefaultSpacingGrid>
          {!useDeclaredValue && (
            <Box sx={{ mt: 2 }}>
              <MRTDataGridv2
                columns={[
                  {
                    accessorKey: 'productDescription',
                    header: formatMessage(messages.productDescription),
                  },
                  {
                    accessorKey: 'quantity',
                    header: formatMessage(messages.quantity),
                  },
                  {
                    accessorKey: 'unitPrice',
                    header: formatMessage(messages.unitPrice),
                    Cell: ({ cell }: any) =>
                      appendCurrency(
                        formatValue(cell.getValue()),
                        getCurrencySymbol(currencyId) || ''
                      ),
                  },
                  {
                    accessorKey: 'total',
                    header: 'Total',
                    Cell: ({ row }: any) => {
                      return appendCurrency(
                        formatValue(row.original.total),
                        getCurrencySymbol(currencyId) || ''
                      )
                    },
                  },
                  {
                    accessorKey: 'countryOfOriginId',
                    header: formatMessage(messages.countryOfOrigin),
                    Cell: ({ cell }: any) => {
                      const countryId = cell.getValue()
                      const country = countries.find((c: any) => c.id === countryId)
                      return country ? country.name : 'N/A'
                    },
                  },
                  {
                    id: 'actions',
                    header: '',
                    size: 40,
                    Cell: ({ row }: any) => (
                      <IconButton
                        color={'info'}
                        onClick={() => handleOpenEditModal(row.original.id)}
                      >
                        <Visibility />
                      </IconButton>
                    ),
                  },
                ]}
                data={fields.map((product: any, idx: number) => {
                  const total =
                    product.quantity !== null &&
                      product.quantity !== undefined &&
                      product.unitPrice !== null &&
                      product.unitPrice !== undefined
                      ? product.quantity * product.unitPrice
                      : 'N/A'

                  return {
                    ...product,
                    id: idx,
                    total: total,
                  }
                })}
                enableColumnActions={false}
                enableColumnFilters={false}
                enablePagination={false}
                enableSorting={false}
                enableBottomToolbar={false}
                enableTopToolbar={false}
                componentPadding='0'
                muiTableBodyRowProps={{
                  sx: {
                    height: '32px',
                    maxHeight: '32px',
                    minHeight: '32px',
                  },
                }}
                muiTableBodyCellProps={{
                  sx: {
                    height: '32px',
                    maxHeight: '32px',
                    minHeight: '32px',
                    paddingTop: 0,
                    paddingBottom: 0,
                    overflow: 'hidden',
                  },
                }}
                state={{ isLoading: false }}
                getRowId={(row: any) => row.id}
              />
              <SideModalWrapper
                open={isEditModalOpen}
                onClose={handleCloseEditModal}
                headerLabel={formatMessage(messages.productDescription)}
                withBottomBox={false}
              >
                {editProductIndex !== null && (
                  <Box sx={{ mt: '72px' }}>
                    <LoadContentTabs
                      prefix={`bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${editProductIndex}`}
                      bookingIndex={consignmentIndex}
                    />
                  </Box>
                )}
              </SideModalWrapper>
            </Box>
          )}
        </ToggleContainer>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ mt: '-8px' }}>
            {!isOpenSummaryContent && (
              <Button
                onClick={() => setIsOpenSummaryContent((prev) => !prev)}
                sx={{ textTransform: 'none' }}
              >
                {formatMessage(messages.openPackageContent, { loadType: loadTypeString })}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        p: '10px 0 16px 24px',
      }}
    >
      <ToggleContainer
        title={formatMessage(messages.packageContent, { loadType: loadTypeString })}
        isOpen={isOpenPackageContent}
        handleToggle={() => {
          handleTogglePackageContent(handleAddProductIfRequired)
        }}
        sx={{ mb: '16px' }}
      >
        <DefaultSpacingGrid container>
          <Grid item xs={12} md={!useDeclaredValue ? 10 : 12}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography sx={{ color: '#636363' }}>
                  {formatMessage(messages.declaredValue)}
                </Typography>
                <Controller
                  name={`bookings.${consignmentIndex}.packaging.${packagingIndex}.useDeclaredValue`}
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <Switch
                      checked={!field.value}
                      disabled={disabled}
                      onChange={(e, checked) => {
                        handleToggleWithConfirmation(checked)
                      }}
                    />
                  )}
                />
                <Typography sx={{ color: '#636363' }}>
                  {formatMessage(messages.specifyContent)}
                </Typography>
              </Box>
              <Box>
                <Controller
                  name={`bookings.${consignmentIndex}.packaging.${packagingIndex}.isHazardous`}
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          {...field}
                          checked={field.value}
                          disabled={disabled}
                          onChange={(e) => {
                            field.onChange(e)
                          }}
                        />
                      }
                      label={formatMessage(messages.isHazardous)}
                    />
                  )}
                />
              </Box>
            </Box>
          </Grid>
          {useDeclaredValue ? (
            <>
              <Grid item xs={12} md={4}>
                <FormInput
                  name={`bookings.${consignmentIndex}.packaging.${packagingIndex}.declaredContentDescription`}
                  label={formatMessage(messages.contentDescription)}
                  required={true}
                  disabled={disabled}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInput
                  name={`bookings.${consignmentIndex}.packaging.${packagingIndex}.declaredValue`}
                  label={formatMessage(messages.totalValue)}
                  required={true}
                  disabled={disabled}
                  numberFormatProps={{
                    decimalScale: 2,
                  }}
                  InputLabelProps={{ shrink: true }}
                  type='number'
                  unit={currencies.find((currency) => currency.id === currencyId)?.localisedSymbol}
                  fullWidth
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={2} container justifyContent='flex-end'>
                <Button
                  startIcon={<AddCircleOutlinedIcon />}
                  disabled={disabled}
                  onClick={() =>
                    appendProduct({
                      internalId: uuidv4(),
                      productDescription: '',
                      quantity: 1,
                      unitPrice: 0,
                      totalValue: undefined,
                      useProduct: false,
                    })
                  }
                  sx={{
                    textTransform: 'none',
                    paddingTop: '8px',
                    marginBottom: '12px',
                  }}
                >
                  {formatMessage(messages.addItem)}
                </Button>
              </Grid>
              {fields.map((field, productIndex) => (
                <Grid item xs={12} md={12} key={field.id}>
                  <ProductForm
                    consignmentIndex={consignmentIndex}
                    packagingIndex={packagingIndex}
                    productIndex={productIndex}
                    removeProduct={handleRemoveProduct}
                    disabled={disabled}
                    productsCount={fields.length}
                  />
                </Grid>
              ))}
            </>
          )}
        </DefaultSpacingGrid>
      </ToggleContainer>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Box sx={{ mt: '-8px' }}>
          {!isOpenPackageContent && (
            <Button
              onClick={() => handleTogglePackageContent(handleAddProductIfRequired)}
              sx={{ textTransform: 'none' }}
            >
              {formatMessage(messages.showContents)}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default PackageContentForm
